(() => {
  if (customElements.get('stellar-snapshot-v2')) return;
  const allowedHosts = new Set(['cdn.shopify.com', 'cdn.gotolstoy.com', 'videos.gotolstoy.com', 'widget-static.onecommerce.io', 'scontent-ord5-1.cdninstagram.com', 'scontent-ord5-2.cdninstagram.com', 'scontent-ord5-3.cdninstagram.com']);
  // Public, token-free cache. The theme never calls Instagram's authenticated API.
  const instagramFeedUrl = 'https://feed.stellarhair.au/feed.php';
  const safeInstagramUrl = (value, social = false) => {
    try {
      if (typeof value !== 'string' || /[\u0000-\u0020\u007f]/.test(value)) return '';
      const url = new URL(value), authority = value.match(/^https:\/\/([^/]*)(?:\/|$)/i)?.[1];
      if (url.protocol !== 'https:' || url.username || url.password || !authority || authority.includes(':') || authority.includes('@')) return '';
      const hosts = social ? ['instagram.com','www.instagram.com'] : ['cdninstagram.com','fbcdn.net'];
      if (!hosts.some(host => url.hostname === host || (!social && url.hostname.endsWith('.'+host)))) return '';
      for (const key of url.searchParams.keys()) if (/access_token|authorization|secret/i.test(key)) return '';
      return url.href;
    } catch { return ''; }
  };
  class InstagramFeed {
    static normalize(data, now = Date.now()) {
      const iso = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?(?:Z|[+-]\d\d:\d\d)$/.test(value) && Number.isFinite(Date.parse(value));
      if (!data || data.schemaVersion !== 1 || data.source !== 'instagram' || !iso(data.updatedAt) || typeof data.stale !== 'boolean' || !Array.isArray(data.items) || !data.items.length || data.items.length > 2000) throw Error('feed');
      const age = now - Date.parse(data.updatedAt);
      if (age < -300000 || age > 72*60*60*1000) throw Error('feed');
      const ids = new Set();
      const media = item => {
        if (!item || !['image','video'].includes(item.type)) throw Error('feed');
        const url = safeInstagramUrl(item.url), poster = safeInstagramUrl(item.poster);
        if (!url || !poster) throw Error('feed');
        return {type:item.type,url,poster};
      };
      const items = data.items.map(item => {
        if (!item || typeof item.id !== 'string' || !/^\d{1,50}$/.test(item.id) || ids.has(item.id) || typeof item.caption !== 'string' || item.caption.length > 100000 || !iso(item.date) || !Array.isArray(item.album) || item.album.length > 100) throw Error('feed');
        const original = safeInstagramUrl(item.original,true);
        if (!original) throw Error('feed');
        ids.add(item.id);
        return {id:item.id,...media(item),caption:item.caption,date:item.date,original,album:item.album.map(media),products:[]};
      });
      // Keep server order, including deterministic ordering for equal timestamps.
      for (let i=1;i<items.length;i++) if (Date.parse(items[i].date)>Date.parse(items[i-1].date)) throw Error('feed');
      return {items,updatedAt:data.updatedAt,stale:data.stale};
    }
    static async load(snapshot, fetcher, signal, timeoutMs = 6000) {
      const controller = new AbortController(), abort = () => controller.abort();
      signal.addEventListener('abort',abort,{once:true}); if (signal.aborted) abort();
      const timer = setTimeout(abort,timeoutMs), maxBytes=4*1024*1024;
      try {
        const response = await fetcher(instagramFeedUrl,{method:'GET',mode:'cors',credentials:'omit',redirect:'error',signal:controller.signal,headers:{Accept:'application/json'}});
        if (!response.ok || !/^application\/json(?:\s*;|$)/i.test(response.headers.get('content-type') || '') || Number(response.headers.get('content-length')) > maxBytes) throw Error('feed');
        let text = '';
        if (response.body?.getReader) {
          const reader = response.body.getReader(), decoder = new TextDecoder(); let bytes=0;
          try {
            while (true) {
              const part = await reader.read(); if (part.done) break;
              bytes += part.value.byteLength; if (bytes > maxBytes) { controller.abort(); throw Error('feed'); }
              text += decoder.decode(part.value,{stream:true});
            }
            text += decoder.decode();
          } finally { reader.releaseLock(); }
        } else {
          text = await response.text();
          if (new TextEncoder().encode(text).byteLength > maxBytes) throw Error('feed');
        }
        if (controller.signal.aborted) throw Error('feed');
        const feed = InstagramFeed.normalize(JSON.parse(text));
        return {mode:'social',format:'stellar-instagram-feed-v1',profile:snapshot.profile,products:{},...feed};
      } catch {
        return snapshot;
      } finally {
        clearTimeout(timer);signal.removeEventListener('abort',abort);
      }
    }
  }

  const safeMedia = value => { try { const url = new URL(value, location.origin); const storefrontCdn = url.origin === location.origin && (/^\/cdn\/shop\/(?:videos|files|products|collections)\//.test(url.pathname) || /^\/cdn\/shop\/t\/\d+\/assets\/stellar-social-fallback-\d{8}-\d{2}\.jpg$/.test(url.pathname)); return url.protocol === 'https:' && !url.username && !url.password && (allowedHosts.has(url.hostname) || storefrontCdn || safeInstagramUrl(value)) ? url.href : ''; } catch { return ''; } };
  const safeSocial = value => { try { const url = new URL(value); return url.protocol === 'https:' && ['www.instagram.com', 'instagram.com', 'www.tiktok.com', 'tiktok.com'].includes(url.hostname) ? url.href : ''; } catch { return ''; } };
  const wrap = (value, count) => ((value % count) + count) % count;
  const node = (tag, className, text) => { const result = document.createElement(tag); if (className) result.className = className; if (text !== undefined) result.textContent = text; return result; };
  const button = (label, action) => { const result = node('button', 'ssv2__button', label); result.type = 'button'; result.dataset.action = action; return result; };
  const rootPath = value => { const url = new URL(value || '/', location.origin); if (url.origin !== location.origin) throw Error('Invalid store route'); return url.pathname.replace(/\/?$/, '/'); };
  const productPath = (root, handle) => root + 'products/' + encodeURIComponent(handle);
  class SocialPages {
    constructor(data, urls, manifest, fetcher, signal) {
      if (data.format !== 'stellar-social-pages-v1' || !Number.isInteger(data.total) || data.total < 1 || data.total > 10000 || !Number.isInteger(data.pageSize) || data.pageSize < 1 || data.pageSize > 100 || data.pageCount !== Math.ceil(data.total / data.pageSize) || !Array.isArray(urls) || urls.length !== data.pageCount || typeof data.snapshot !== 'string') throw Error('manifest');
      const origin = new URL(manifest, location.origin).origin;
      this.urls = urls.map((value,index) => { const url = new URL(value, location.origin); if (url.protocol !== 'https:' || url.origin !== origin || !url.pathname.endsWith('/stellar-native-snapshot-v2-social-page-'+(index+1)+'.json')) throw Error('manifest'); return url.href; });
      this.data=data;this.fetcher=fetcher;this.signal=signal;this.items=Array(data.total);this.loaded=new Set();this.pending=new Map();this.ids=new Map();
    }
    async ensure(start,end) {
      if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end < start || end > this.items.length) throw Error('page');
      if (start===end) return;
      const pages=[];for(let i=Math.floor(start/this.data.pageSize);i<=Math.floor((end-1)/this.data.pageSize);i++)pages.push(this.page(i));
      await Promise.all(pages);
    }
    page(index) {
      if(this.loaded.has(index))return Promise.resolve();
      if(this.pending.has(index))return this.pending.get(index);
      const request=this.fetchPage(index);this.pending.set(index,request);
      return request.finally(()=>{if(this.pending.get(index)===request)this.pending.delete(index)});
    }
    async fetchPage(index) {
      const controller=new AbortController(),abort=()=>controller.abort();
      this.signal.addEventListener('abort',abort,{once:true});if(this.signal.aborted)abort();
      const timer=setTimeout(abort,15000);
      try {
        const response=await this.fetcher(this.urls[index],{signal:controller.signal,credentials:'omit'});
        if(!response.ok)throw Error('page');const page=await response.json();
        if(controller.signal.aborted)throw new DOMException('Aborted','AbortError');
        const offset=index*this.data.pageSize,count=Math.min(this.data.pageSize,this.data.total-offset);
        if(page.snapshot!==this.data.snapshot||page.page!==index||!Array.isArray(page.items)||page.items.length!==count)throw Error('page');
        const ids=new Set();for(const item of page.items){if(!item||typeof item.id!=='string'||!item.id||ids.has(item.id)||(this.ids.has(item.id)&&this.ids.get(item.id)!==index))throw Error('page');ids.add(item.id);}
        page.items.forEach((item,i)=>{this.items[offset+i]=item;this.ids.set(item.id,index)});this.loaded.add(index);
      } finally {clearTimeout(timer);this.signal.removeEventListener('abort',abort)}
    }
  }

  class CartClient {
    constructor(fetcher) { this.fetcher = fetcher; this.pending = false; this.uncertain = false; }
    async product(root, product, signal) {
      const response = await this.fetcher(productPath(root, product.handle) + '.js', {credentials:'same-origin', signal, headers:{Accept:'application/json'}, cache:'no-store'});
      if (!response.ok) throw Error(response.status===404?'product_unavailable':'product_error');
      const data = await response.json();
      if (String(data.id) !== String(product.id) || !Array.isArray(data.variants)) throw Error('product_error');
      return data;
    }
    reviews(root, product) {
      this.reviewCache ||= new Map();
      const key = productPath(root, product.handle);
      if (this.reviewCache.has(key)) return this.reviewCache.get(key);
      const request = this.fetcher(key, {credentials:'same-origin', headers:{Accept:'text/html'}}).then(async response => {
        if (!response.ok || (response.url && new URL(response.url, location.origin).origin !== location.origin)) return null;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const figure = [...doc.querySelectorAll('figure.stellar-customer-highlight[data-review-product]')].find(el => el.dataset.reviewProduct === String(product.id));
        const widget = [...doc.querySelectorAll('.stellar-product-price-rating__reviews .jdgm-preview-badge[data-id]')].find(el => el.dataset.id === String(product.id));
        const badge = widget?.querySelector('.jdgm-prev-badge'), rating = Number(badge?.dataset.averageRating), count = Number(badge?.dataset.numberOfReviews);
        const caption = figure?.querySelector('figcaption'), quote = figure?.querySelector('blockquote')?.textContent.trim();
        const attribution = caption ? [...caption.childNodes].filter(el => el.nodeName !== 'A').map(el => el.textContent).join('').trim().replace(/[·\s]+$/, '') : '';
        return {
          highlight: quote && attribution ? {label:figure.querySelector('.stellar-customer-highlight__label')?.textContent.trim() || '', quote, attribution, readLabel:caption.querySelector('a')?.textContent.trim() || ''} : null,
          rating: Number.isFinite(rating) && rating >= 0 && rating <= 5 && Number.isInteger(count) && count > 0 ? {value:rating, count, text:badge.querySelector('.jdgm-prev-badge__text')?.textContent.trim() || String(count)} : null
        };
      }).catch(() => null);
      this.reviewCache.set(key, request);
      return request;
    }
    async add() { throw Error('review_checkout'); }
  }
  class ThemeCommerce {
    constructor(owner) { this.owner = owner; this.pending = false; this.revision = 0; }
    prepare() {
      if (this.modal?.isConnected) return this.modal;
      const Base = customElements.get('quick-add-modal');
      const dialogBase = Base && Object.getPrototypeOf(Base.prototype);
      const template = this.owner.querySelector('template[data-commerce-modal]');
      if (!Base || !dialogBase?.show || !template) throw Error('options_error');
      const fragment = template.content.cloneNode(true);
      const modal = fragment.querySelector('quick-add-modal');
      document.body.append(fragment);
      if (!modal?.modalContent || !['preventDuplicatedIDs','removeDOMElements','setInnerHTML','preventVariantURLSwitching','removeGalleryListSemantic','updateImageSizes'].every(key => typeof modal[key] === 'function')) {
        modal?.remove(); throw Error('options_error');
      }
      this.modal = modal; this.dialogBase = dialogBase;
      const hide = modal.hide.bind(modal);
      modal.hide = (...args) => {
        this.cancel(); this.slider?.destroy?.(); this.slider = null;
        hide(...args); this.owner.updatePreview();
      };
      return modal;
    }
    cancel() { this.revision++; this.controller?.abort(); this.pending = false; }
    async open(product, variantId, opener, directAdd = false) {
      if (this.pending || !this.owner.isConnected || this.owner.dataset.previewOnly !== 'true' || this.owner.dataset.customerB2b === 'true') return false;
      const modal = this.prepare();
      this.pending = true; const revision = ++this.revision;
      this.controller = new AbortController(); const signal = this.controller.signal;
      const timer = setTimeout(() => this.controller?.abort(), 15000);
      try {
        const current = await cart.product(this.owner.root, product, signal);
        const variant = current.variants.find(item => String(item.id) === String(variantId));
        if (!variant?.available || current.requires_selling_plan) throw Error('selection_changed');
        const url = productPath(this.owner.root, product.handle) + '?variant=' + encodeURIComponent(String(variant.id));
        const response = await fetch(url, {credentials:'same-origin', signal, cache:'no-store'});
        if (!response.ok || (response.url && new URL(response.url, location.origin).origin !== location.origin)) throw Error('options_error');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted || revision !== this.revision || !this.owner.isConnected || !modal.isConnected) return false;
        const productElement = html.querySelector('section[id^="MainProduct-"]');
        const submit = productElement?.querySelector('button[id^="ProductSubmitButton-"][name="add"]');
        const form = submit?.closest('form');
        if (form?.querySelector('[name="id"]')?.value !== String(variant.id) || !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') throw Error('selection_changed');
        modal.productElement = productElement;
        modal.preventDuplicatedIDs(); modal.removeDOMElements();
        const source = document.createElement('button'); source.dataset.productUrl = url;
        modal.setInnerHTML(modal.modalContent, modal.productElement.innerHTML, source);
        modal.removeGalleryListSemantic(); modal.updateImageSizes(); modal.preventVariantURLSwitching();
        window.Shopify?.PaymentButton?.init?.(); window.ProductModel?.loadShopifyXR?.();
        if (signal.aborted || revision !== this.revision || !this.owner.isConnected) return false;
        this.owner.pauseAll();
        if (directAdd) {
          const addButton = modal.querySelector('button[id^="ProductSubmitButton-"][name="add"]');
          const productForm = addButton?.closest('product-form'), liveForm = addButton?.closest('form');
          if (!liveForm || !addButton || typeof productForm.onSubmitHandler !== 'function' || typeof addButton.click !== 'function') throw Error('options_error');
          const invalidField = [...liveForm.elements].some(field => field.willValidate && !field.validity.valid);
          if (!invalidField && !addButton.disabled && addButton.getAttribute('aria-disabled') !== 'true') {
            let complete;
            const done = new Promise(resolve => { complete = resolve; });
            const finished = () => {
              if (!productForm.submitting) { liveForm.removeEventListener('stellar:purchase-state',finished); complete(); }
            };
            liveForm.addEventListener('stellar:purchase-state',finished);
            modal.openedBy = opener;
            // Existing form handler owns properties, add-ons, Ajax and cart recovery.
            addButton.click();
            if (productForm.submitting) {
              if (this.owner.dialog?.open) this.owner.dialog.close();
              await done;
              return true;
            }
            liveForm.removeEventListener('stellar:purchase-state',finished);
          }
          // Required choices, engraving validation or cart-review state remain visible.
        }
        if (this.owner.dialog?.open) this.owner.dialog.close();
        this.dialogBase.show.call(modal, opener);
        const gallery = modal.querySelector('.js-media-list');
        if (gallery && typeof window.Swiper === 'function') this.slider = new window.Swiper(gallery, {slidesPerView:1,autoHeight:true,navigation:{nextEl:modal.querySelector('.swiper-btn--next'),prevEl:modal.querySelector('.swiper-btn--prev')},pagination:{el:modal.querySelector('.swiper-pagination'),clickable:true,type:'bullets'},on:{slideChange:()=>window.pauseAllMedia?.()}});
        return true;
      } finally { clearTimeout(timer); if (revision === this.revision) this.pending = false; }
    }
    destroy() {
      this.cancel(); this.slider?.destroy?.();
      if (this.modal?.hasAttribute('open')) this.modal.hide(true);
      this.modal?.remove(); this.modal = null;
    }
  }

  const cart = new CartClient((...args) => fetch(...args));
  const core = Object.freeze({CartClient, ThemeCommerce, SocialPages, InstagramFeed, safeMedia, safeSocial, wrap, rootPath});
  Object.defineProperty(window, 'StellarSnapshotV2Core', {value:core, configurable:true});
  class Snapshot extends HTMLElement {
    connectedCallback() {
      if (this.dataset.previewOnly !== 'true') return;
      this.lifecycle?.abort();
      this.dialog?.remove(); this.dialog = null;
      this.commerce?.destroy(); this.commerce = new ThemeCommerce(this);
      this.lifecycle = new AbortController();
      this.generation = (this.generation || 0) + 1;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.stage = this.querySelector('[data-stage]');
      this.status = this.querySelector('[data-status]');
      this.root = rootPath(this.dataset.root);
      this.mode = this.dataset.mode;
      if (this.mode === 'stories') this.listen(document, 'stellar:story-library-change', () => {
        this.pauseAll(); this.dialog?.close(); this.renderAbort?.abort();
        this.generation += 1; this.loading = false; this.data = null; this.index = 0;
        this.load();
      });
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion = this.dataset.motion === 'true' && !this.reduced.matches;
      this.index = 0; this.page = 0; this.visibleCount = 0; this.expandedSocialCount = 0; this.soundItem = null; this.previewChoices = new Map(); this.previewTimes = new Map(); this.inViewport = false; this.pages=null; this.socialBusy=false;
      this.listen(this, 'click', event => this.click(event));
      this.listen(this, 'change', event => { if (event.target.matches('[data-variant]')) this.variantChanged(event.target); });
      this.listen(document, 'visibilitychange', () => { if (document.hidden) this.pauseAll(); else this.updatePreview(); });
      this.listen(this.mobile, 'change', () => this.resizeLayout());
      this.listen(this.reduced, 'change', () => { if (this.reduced.matches) { this.motion = false; this.previewChoices?.clear(); } else if (this.mode === 'stories') this.motion = this.dataset.motion === 'true'; this.updatePreview(); this.updateMotionButton(); });
      if (this.mode === 'social') this.renderSocialLoading();
      if ('IntersectionObserver' in window) {
        const lead=this.mode==='social'?Math.min(2400,Math.max(1000,2*(window.innerHeight||800))):300;
        this.loadObserver = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) { this.loadObserver.disconnect(); this.load(); } }, {rootMargin:lead+'px 0px'});
        this.loadObserver.observe(this);
        this.viewObserver = new IntersectionObserver(entries => { this.inViewport = entries.some(e => e.isIntersecting); this.updatePreview(); }, {threshold:0.15});
        this.viewObserver.observe(this);
      } else { this.inViewport = true; this.load(); }
      if (this.mode === 'stories') this.load();
    }
    listen(target, name, callback, extra = {}) { target.addEventListener(name, callback, {...extra, signal:this.lifecycle.signal}); }
    text(key) { return this.labels[key] || key; }
    renderSocialLoading() {
      if (this.mode !== 'social') return;
      const grid = node('ul', 'ssv2__track ssv2__loading-grid');
      grid.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < this.socialPageSize(); i++) {
        const tile = node('li', 'ssv2__tile');
        tile.append(node('span', 'ssv2__trigger'));
        grid.append(tile);
      }
      const label = node('span', 'ssv2__loading-label', this.text('loading'));
      label.setAttribute('role', 'status');
      this.stage.replaceChildren(grid, label, node('div', 'ssv2__loading-controls'));
    }
    spotlightEditorFeed(data) {
      const element = this.closest('#stellar-video-spotlight')?.querySelector('[data-spotlight-library]');
      if (!element) return data;
      const config = JSON.parse(element.textContent);
      if (!config.enabled || !Array.isArray(config.items)) return data;
      const originals = new Map((data.items || []).map(item => [item.id, item]));
      const items = [], products = {...data.products}, seen = new Set();
      for (const entry of config.items) {
        if (!entry.enabled) continue;
        const original = originals.get(entry.originalId);
        const url = entry.url || original?.url;
        if (!safeMedia(url) || seen.has(url)) continue;
        seen.add(url);
        const replaced = !!entry.url && entry.url !== original?.url;
        const tags = !original || entry.overrideProducts ? (entry.products || []).map(product => ({id:product.id,variantIds:[]})) : original.products;
        for (const product of entry.products || []) products[product.id] = product;
        items.push({...original, id:'featured-' + entry.blockId, type:'video', url,
          preview:replaced ? url : original?.preview || url,
          poster:entry.poster || (replaced ? '' : original?.poster || ''),
          caption:entry.caption || original?.caption || '',
          captions:entry.removeSubtitles ? '' : entry.captions || ((!replaced || entry.keepOriginalSubtitles) ? original?.captions || '' : ''),
          captionLanguage:entry.captionLanguage || original?.captionLanguage || 'en',
          captionLabel:entry.captionLabel || original?.captionLabel || '',
          products:tags || [], editorBlockId:entry.blockId});
      }
      return items.length ? {...data,items,products,order:config.order,editorManaged:true} : data;
    }
    async load() {
      if (this.loading || this.data) return;
      this.loading = true; this.stage.setAttribute('aria-busy','true');
      if (this.mode === 'social' && !this.track) this.renderSocialLoading();
      const generation = this.generation;
      try {
        const response = await fetch(this.dataset.manifest, {signal:this.lifecycle.signal, credentials:'omit'});
        if (!response.ok) throw Error('manifest');
        let data = await response.json();
        if (this.mode === 'spotlight') data = this.spotlightEditorFeed(data);
        // Dated, public poster snapshot: resolve theme assets without credentials or expiring media URLs.
        if (this.mode === 'social' && data.format === 'stellar-social-local-v1') {
          const base = new URL(this.dataset.manifest, location.href);
          data.items = data.items.map(item => {
            if (!/^stellar-social-fallback-\d{8}-\d{2}\.jpg$/.test(item.poster) || item.url !== item.poster) throw Error('snapshot');
            const poster = new URL(item.poster, base).href;
            return {...item, poster, url:poster};
          });
        }
        if (this.mode === 'social' && this.dataset.remoteFeedEnabled === 'true') {
          data = await InstagramFeed.load(data, (...args) => fetch(...args), this.lifecycle.signal);
        }
        if (!this.isConnected || generation !== this.generation) return;
        this.data = data;
        const feed = this.mode === 'stories' ? (window.StellarStoryLibrary ? window.StellarStoryLibrary.feed(data, this.dataset.product) : data.feeds[this.dataset.product]) : data;
        if (this.mode==='social' && data.format==='stellar-social-pages-v1') {
          const urls=JSON.parse(this.querySelector('[data-social-pages]').textContent);
          this.pages=new SocialPages(data,urls,this.dataset.manifest,(...args)=>fetch(...args),this.lifecycle.signal);this.items=this.pages.items;
          await this.pages.ensure(0,Math.min(data.total,this.socialPageSize()));
          if(!this.isConnected||generation!==this.generation)return;
        } else {
          if (!feed || feed.disabled || !feed.items?.length) { this.hidden = true; return; }
          this.items = [...feed.items];
        }
        if ((this.mode === 'spotlight' && !feed.editorManaged) || (this.mode !== 'social' && feed.order === 'random')) {
          for (let i = this.items.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [this.items[i], this.items[j]] = [this.items[j], this.items[i]]; }
        }
        if (this.mode === 'spotlight') this.index = feed.editorManaged && feed.order !== 'random' ? 0 : Math.floor(this.items.length / 2);
        if (this.mode === 'social') this.visibleCount = Math.min(this.items.length, this.socialPageSize());
        if (this.mode === 'stories') this.hidden = false;
        this.render();
        this.status.textContent = '';
      } catch (error) { if (error.name !== 'AbortError' && this.isConnected && generation===this.generation) { this.data=null;this.pages=null;this.status.textContent=this.text('unavailable');this.stage.replaceChildren(button(this.text('retry'),'retry')); } }
      finally { this.loading = false; this.stage.setAttribute('aria-busy', String(this.mode==='social'&&!!this.imageWarming)); }
    }
    image(item, alt) {
      const img = node('img'); const url = safeMedia(item.poster || (item.type === 'image' ? item.url : ''));
      img.alt = alt || ''; img.width = 450; img.height = 750; img.loading = 'lazy'; img.decoding = 'async';
      if (this.mode === 'social') this.prepareSocialImage(img);
      if (this.mode === 'social') img.addEventListener('error', () => {
        img.hidden = true;
        if (img.parentElement && !img.parentElement.querySelector('[data-media-error]')) {
          const message = node('span', 'ssv2__status', this.text('unavailable')); message.dataset.mediaError = ''; img.parentElement.append(message);
        }
      }, {once:true});
      if (url && this.mode === 'stories') {
        const poster = new URL(url);
        if (poster.hostname === 'cdn.shopify.com' && poster.pathname.startsWith('/s/files/') && /\.(jpe?g|png|webp|avif)$/i.test(poster.pathname)) {
          const sized = width => { const source = new URL(url); source.searchParams.set('width', String(width)); return source.href; };
          img.srcset = [180, 360, 540].map(width => sized(width) + ' ' + width + 'w').join(', ');
          img.sizes = 'auto, 180px';
          img.addEventListener('error', () => { img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = url; }, {once:true});
          img.src = sized(360);
        } else img.src = url;
      } else if (url) img.src = url;
      else { img.hidden = true; }
      return img;
    }
    prepareSocialImage(img) {
      img.loading='eager';img.fetchPriority='low';
      const fit=this.dataset.mediaFit==='contain'?'contain':'cover',focal={center:'50% 50%',top:'50% 0%',bottom:'50% 100%'}[this.dataset.mediaFocal]||'50% 50%';
      img.style.objectFit=fit;img.style.objectPosition=focal;
      img._ssv2Ready=new Promise(resolve=>{
        let done=false;const ready=()=>{if(done)return;done=true;img.dataset.imageReady='true';resolve();};
        const loaded=()=>{img.dataset.orientation=img.naturalWidth===img.naturalHeight?'square':img.naturalWidth>img.naturalHeight?'landscape':'portrait';Promise.resolve(img.decode?.()).catch(()=>{}).then(ready);};
        img.addEventListener('load',loaded,{once:true,signal:this.lifecycle.signal});
        img.addEventListener('error',ready,{once:true,signal:this.lifecycle.signal});
        this.lifecycle.signal.addEventListener('abort',ready,{once:true});
        queueMicrotask(()=>{if(img.complete&&img.naturalWidth>0)loaded();});
      });
    }
    prepareSocialGrid() {
      if(this.mode!=='social')return;
      const track=this.track,generation=this.generation;this.imageWarming=true;this.stage.setAttribute('aria-busy','true');
      const images=[...track.querySelectorAll('.ssv2__trigger>img')];let timer;
      const deadline=new Promise(resolve=>{timer=setTimeout(resolve,2000);this.lifecycle.signal.addEventListener('abort',()=>{clearTimeout(timer);resolve();},{once:true});});
      Promise.race([Promise.allSettled(images.map(img=>img._ssv2Ready)),deadline]).then(()=>{
        clearTimeout(timer);if(!this.isConnected||this.generation!==generation||this.track!==track)return;
        this.imageWarming=false;this.stage.setAttribute('aria-busy','false');
      });
    }
    tile(item, index, clone = false) {
      const li = node('li', 'ssv2__tile'); li.dataset.index = index;
      if (clone) { li.dataset.clone = 'true'; li.setAttribute('aria-hidden', 'true'); }
      const trigger = button('', this.mode === 'spotlight' ? 'preview-toggle' : 'open'); trigger.className = 'ssv2__trigger'; trigger.dataset.index = index;
      trigger.setAttribute('aria-label', (this.mode === 'social' ? this.text('open') : this.text('watch')) + ': ' + (item.caption || String(index + 1)).slice(0,180));
      if (this.mode !== 'spotlight') trigger.setAttribute('aria-haspopup','dialog');
      if (clone) trigger.tabIndex = -1;
      const poster=this.image(item, '');trigger.append(poster);
      const preview = node('video', 'ssv2__preview'); preview.muted = true; preview.playsInline = true; preview.preload = 'none'; preview.tabIndex = -1; preview.setAttribute('aria-hidden','true');
      if (this.mode === 'spotlight') {
        preview.dataset.itemId = item.id;
        for (const event of ['play','pause','error']) preview.addEventListener(event,()=>this.updatePlaybackButtons(),{signal:this.renderAbort.signal});
        const fit=(width,height)=>{
          if(!Number.isFinite(width)||!Number.isFinite(height)||width<=0||height<=0)return;
          li.style.setProperty('--ssv2-media-ratio',String(width/height));li.dataset.mediaRatio='true';
        };
        const posterFit=()=>{if(!preview.videoWidth)fit(poster.naturalWidth,poster.naturalHeight)};
        poster.addEventListener('load',posterFit,{signal:this.renderAbort.signal});
        if(poster.complete)posterFit();
        preview.addEventListener('loadedmetadata',()=>fit(preview.videoWidth,preview.videoHeight),{signal:this.renderAbort.signal});
        preview.addEventListener('loadeddata',()=>{if(preview.getAttribute('src')&&preview.readyState>=2)trigger.dataset.videoReady='true'},{signal:this.renderAbort.signal});
        for(const event of ['emptied','error'])preview.addEventListener(event,()=>{delete trigger.dataset.videoReady},{signal:this.renderAbort.signal});
      }
      trigger.append(preview);
      if (this.mode === 'spotlight') {
        const indicator=node('span','ssv2__play-indicator','\u25B6'); indicator.setAttribute('aria-hidden','true'); trigger.append(indicator);
      }
      if (this.mode === 'social') {
        const overlay = node('span', 'ssv2__overlay');
        overlay.append(node('span', 'ssv2__caption-preview', item.caption || ''), node('time', '', this.date(item.date)));
        trigger.append(overlay);
        if (item.album?.length) trigger.append(node('span','ssv2__badge',this.text('album')));
        if (item.type === 'video') trigger.append(node('span','ssv2__badge',this.text('watch')));
      }
      li.append(trigger);
      if (this.mode === 'spotlight') {
        const shop=button(this.text(item.products?.length?'shop_tools':'learn'),'open');
        shop.className='ssv2__shop'; shop.dataset.index=index; shop.setAttribute('aria-haspopup','dialog'); if (clone) shop.tabIndex=-1;
        li.append(shop,this.soundButton(index,clone));
      }
      return li;
    }
    render() {
      this.storyResize?.disconnect();
      clearTimeout(this.scrollTimer); this.spotlightUserScroll = false;
      this.pauseAll(); this.renderAbort?.abort(); this.renderAbort = new AbortController();
      this.stage.replaceChildren();
      this.track = node('ul','ssv2__track'); this.track.tabIndex = 0; this.track.setAttribute('aria-label',this.text('gallery'));
      if (this.mode === 'social') {
        this.items.slice(0, this.visibleCount).forEach((item,i) => this.track.append(this.tile(item,i)));
      } else {
        const buffer = this.mode === 'spotlight' && this.items.length > 1 ? Math.min(4, this.items.length) : 0;
        for (let i = this.items.length - buffer; i < this.items.length; i++) this.track.append(this.tile(this.items[i],i,true));
        this.items.forEach((item,i) => this.track.append(this.tile(item,i)));
        for (let i = 0; i < buffer; i++) this.track.append(this.tile(this.items[i],i,true));
      }
      this.stage.append(this.track);
      const controls = node('div','ssv2__controls');
      if (this.mode === 'social') controls.append(button(this.text('more'),'more'));
      else if (this.mode === 'spotlight') {
        if (!this.mobile.matches) this.stage.append(this.spotlightArrow('previous'),this.spotlightArrow('next'));
      } else if (this.mode === 'stories') {
        this.storyFooter=node('div','ssv2__story-footer');this.storyFooter.hidden=true;
        this.storyProgress=node('span','ssv2__story-progress');this.storyProgress.setAttribute('aria-hidden','true');
        this.storyThumb=node('span','ssv2__story-thumb');this.storyProgress.append(this.storyThumb);
        this.storyMore=button(this.text('more'),'story-more');this.storyMore.className='ssv2__story-more';
        const arrow=document.createElementNS('http://www.w3.org/2000/svg','svg');arrow.setAttribute('viewBox','0 0 20 20');arrow.setAttribute('aria-hidden','true');arrow.setAttribute('focusable','false');
        const path=document.createElementNS('http://www.w3.org/2000/svg','path');path.setAttribute('d','M3 10h13m-5-5 5 5-5 5');path.setAttribute('fill','none');path.setAttribute('stroke','currentColor');path.setAttribute('stroke-width','1.4');path.setAttribute('stroke-linecap','round');path.setAttribute('stroke-linejoin','round');arrow.append(path);this.storyMore.append(arrow);
        this.storyFooter.append(this.storyProgress,this.storyMore);this.stage.append(this.storyFooter);
        if('ResizeObserver' in window){this.storyResize=new ResizeObserver(()=>{this.updateStoryNavigation();this.updatePreview()});this.storyResize.observe(this.track);}
      }
      if (controls.childElementCount) this.stage.append(controls);
      // Only a user gesture may select a different tile after scrolling.
      // Layout changes and centerActive() also emit scroll events.
      for (const event of ['pointerdown','wheel']) this.track.addEventListener(event, () => {
        if (this.mode === 'spotlight') this.spotlightUserScroll = true;
      }, {passive:true, signal:this.renderAbort.signal});
      this.track.addEventListener('scroll', () => {
        if(this.mode==='stories'){this.updateStoryNavigation();this.updatePreview();return;}
        if (this.mode !== 'spotlight' || !this.spotlightUserScroll) return;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.settleSpotlight(),140);
      }, {signal:this.renderAbort.signal});
      this.track.addEventListener('keydown', event => {
        if(this.mode==='stories' && ['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) {
          event.preventDefault();const triggers=[...this.track.querySelectorAll('.ssv2__trigger')],current=triggers.indexOf(document.activeElement);
          const next=event.key==='Home'?0:event.key==='End'?triggers.length-1:Math.max(0,Math.min(triggers.length-1,current+(event.key==='ArrowRight'?1:-1)));
          const target=triggers[next];if(target){target.focus({preventScroll:true});const r=target.getBoundingClientRect(),box=this.track.getBoundingClientRect();this.track.scrollBy({left:r.left+r.width/2-box.left-box.width/2,behavior:this.reduced.matches?'auto':'smooth'});}return;
        }
        if (this.mode === 'social' || !['ArrowLeft','ArrowRight'].includes(event.key) || event.target !== this.track) return;
        event.preventDefault(); this.navigate(event.key === 'ArrowRight' ? 1 : -1);
      }, {signal:this.renderAbort.signal});
      this.applyCenter(this.mode === 'spotlight'); this.updatePagination(); this.updateMotionButton(); this.updateStoryNavigation(); this.updatePreview(); this.prepareSocialGrid();
    }
    updateStoryNavigation() {
      if(this.mode!=='stories'||!this.track||!this.storyFooter)return;
      const width=this.track.clientWidth,total=this.track.scrollWidth,max=Math.max(0,total-width),position=Math.min(max,Math.abs(this.track.scrollLeft));
      this.storyFooter.hidden=max<=2;this.storyMore.hidden=max<=2||position>=max-3;
      const fraction=total?width/total:1;this.storyThumb.style.width=(fraction*100)+'%';
      this.storyThumb.style.transform='translateX('+(max?position/max*(1-fraction)/fraction*100:0)+'%)';
    }
    scrollStories() {
      if(this.mode!=='stories'||!this.track)return;
      const direction=getComputedStyle(this.track).direction==='rtl'?-1:1;
      this.track.scrollBy({left:direction*Math.max(104,this.track.clientWidth-116),behavior:this.reduced.matches?'auto':'smooth'});
    }
    soundButton(index,clone) {
      const control = button('','preview-sound');
      control.className = 'ssv2__sound'; control.dataset.index = index;
      if (clone) control.tabIndex = -1;
      const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
      svg.setAttribute('viewBox','0 0 24 24'); svg.setAttribute('width','20'); svg.setAttribute('height','20'); svg.setAttribute('fill','none'); svg.setAttribute('aria-hidden','true'); svg.setAttribute('focusable','false');
      for (const [kind,d] of [['speaker','M11 5 6 9H3v6h3l5 4V5Z'],['muted','m16 9 5 6m0-6-5 6'],['audible','M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14']]) {
        const path = document.createElementNS('http://www.w3.org/2000/svg','path');
        path.setAttribute('d',d); path.setAttribute('stroke','currentColor'); path.setAttribute('stroke-width','1.7'); path.setAttribute('stroke-linecap','round'); path.setAttribute('stroke-linejoin','round'); path.dataset.soundIcon=kind; svg.append(path);
      }
      control.append(svg); return control;
    }
    updateSoundButtons() {
      if (this.mode !== 'spotlight' || !this.track) return;
      this.track.querySelectorAll('[data-action="preview-sound"]').forEach(control=>{
        const on=this.soundItem === this.items?.[Number(control.dataset.index)]?.id;
        control.setAttribute('aria-pressed',String(on)); control.setAttribute('aria-label',this.text(on?'mute':'unmute'));
        control.querySelector('[data-sound-icon="muted"]').style.display=on?'none':'';
        control.querySelector('[data-sound-icon="audible"]').style.display=on?'':'none';
      });
    }
    spotlightArrow(action) {
      const arrow = button('',action);
      arrow.className = 'ssv2__arrow swiper-arrow swiper-button-' + (action === 'previous' ? 'prev' : 'next') + ' box-shadow';
      arrow.setAttribute('aria-label',this.text(action));
      const svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
      svg.setAttribute('width','16'); svg.setAttribute('height','17'); svg.setAttribute('viewBox','0 0 16 17'); svg.setAttribute('fill','none'); svg.setAttribute('aria-hidden','true'); svg.setAttribute('focusable','false');
      const path = document.createElementNS('http://www.w3.org/2000/svg','path');
      path.setAttribute('d','M2.66675 0.997559C5.33341 6.33089 13.3334 8.66423 13.3334 8.66423C13.3334 8.66423 5.33341 10.9976 2.66675 16.3309'); path.setAttribute('stroke','currentColor');
      svg.append(path); arrow.append(svg); return arrow;
    }
    resizeLayout() {
      if (!this.data || this.loading || !this.items?.length) return;
      if (this.mode !== 'social') { this.render(); return; }
      if (this.socialBusy) { this.breakpointPending = true; return; }
      const end = Math.min(this.items.length, Math.max(this.expandedSocialCount, this.socialPageSize()));
      const apply = () => { this.visibleCount = end; this.render(); };
      if (this.pages && end > this.visibleCount) return this.pageAction(this.visibleCount,end,apply,null);
      apply();
    }
    socialPageSize() { return this.mobile.matches ? 12 : 9; }
    more() {
      if (this.mode !== 'social' || this.socialBusy || this.visibleCount >= this.items.length) return;
      const start=this.visibleCount,end=Math.min(this.items.length,start+(this.mobile.matches?6:12));
      const apply=()=>{this.items.slice(start,end).forEach((item,i)=>this.track.append(this.tile(item,start+i)));this.visibleCount=end;this.expandedSocialCount=end;this.updatePagination()};
      const focus='[data-index="'+start+'"] .ssv2__trigger';
      if (this.pages) return this.pageAction(start,end,apply,focus);
      apply(); this.track.querySelector(focus)?.focus({preventScroll:true});
    }
    updatePagination() {
      if (this.mode !== 'social') return;
      const more = this.querySelector('[data-action="more"]');
      if (more) { more.disabled = this.socialBusy; more.hidden = this.visibleCount >= this.items.length; }
    }
    applyCenter(scroll = true) {
      if (!this.track || this.mode === 'social') return;
      for (const tile of this.track.children) tile.classList.toggle('is-active', !tile.dataset.clone && Number(tile.dataset.index) === this.index);
      if (scroll) this.centerActive();
    }
    centerActive() {
      const active = this.track?.querySelector('.is-active');
      if (active) { const rect=active.getBoundingClientRect(), box=this.track.getBoundingClientRect(); this.track.scrollBy({left:rect.left+rect.width/2-box.left-box.width/2,behavior:'auto'}); }
    }
    settleSpotlight() {
      if (!this.spotlightUserScroll || !this.track?.clientWidth) return;
      this.spotlightUserScroll = false;
      const box=this.track.getBoundingClientRect(),center=box.left+box.width/2;
      const tile=[...this.track.children].reduce((best,t)=>{const r=t.getBoundingClientRect();return Math.abs(r.left+r.width/2-center)<best.distance?{tile:t,distance:Math.abs(r.left+r.width/2-center)}:best},{tile:null,distance:Infinity}).tile;
      if (!tile) return;
      const next = Number(tile.dataset.index);
      if (next === this.index && !tile.dataset.clone) return;
      this.index = next; this.applyCenter(true); this.updatePreview();
    }
    async pageAction(start,end,apply,focus,dialog=false) {
      if(this.socialBusy)return;this.socialBusy=true;
      const generation=this.generation;this.status.textContent=this.text('loading');this.stage.setAttribute('aria-busy','true');
      this.querySelectorAll('[data-action="previous"],[data-action="next"],[data-action="more"],[data-action="post-previous"],[data-action="post-next"]').forEach(el=>el.disabled=true);
      try {
        await this.pages.ensure(start,end);
        if(!this.isConnected||generation!==this.generation||(dialog&&!this.dialog?.open))return;
        apply();this.status.textContent='';
        if(focus){const target=this.querySelector(focus);if(target&&!target.hidden)target.focus({preventScroll:true});else this.track?.focus({preventScroll:true});}
      } catch(error) {if(error.name!=='AbortError'&&this.isConnected&&generation===this.generation)this.status.textContent=this.text('unavailable')}
      finally {if(generation===this.generation){this.socialBusy=false;if(this.status.textContent===this.text('loading'))this.status.textContent='';this.stage.setAttribute('aria-busy','false');this.querySelectorAll('[data-action="more"],[data-action="post-previous"],[data-action="post-next"]').forEach(el=>el.disabled=false);this.updatePagination();if(this.breakpointPending){this.breakpointPending=false;this.resizeLayout()}}}
    }
    movePost(delta,focus='[data-action="close"]') {
      const index=wrap(this.dialogIndex+delta,this.items.length),apply=()=>{this.dialogIndex=index;this.albumIndex=0;this.renderDialog()};
      if(this.pages)return this.pageAction(index,index+1,apply,focus,true);
      apply();this.dialog.querySelector(focus)?.focus({preventScroll:true});
    }

    navigate(delta) {
      if (this.mode === 'social') return;
      clearTimeout(this.scrollTimer); this.spotlightUserScroll = false;
      this.index = wrap(this.index + delta, this.items.length); this.applyCenter(true); this.updatePreview();
    }
    updateMotionButton() {
      const b=this.querySelector('[data-action="motion"]'); if (b) { b.textContent=this.text(this.motion?'pause':'play'); b.setAttribute('aria-pressed',String(this.motion)); }
    }
    rememberPreviewTime(video) {
      if (video?.dataset.itemId && video.getAttribute('src') && Number.isFinite(video.currentTime)) this.previewTimes?.set(video.dataset.itemId,video.currentTime);
    }
    pauseAll() { this.querySelectorAll('video').forEach(video=>{this.rememberPreviewTime(video);video.pause()}); }
    updatePlaybackButtons() {
      if (this.mode !== 'spotlight' || !this.track) return;
      this.track.querySelectorAll('[data-action="preview-toggle"]').forEach(control=>{
        const index=Number(control.dataset.index),video=control.querySelector('video'),item=this.items[index];
        const playing=index===this.index && !control.closest('[data-clone]') && !video.paused;
        control.dataset.paused=String(!playing); control.setAttribute('aria-pressed',String(playing));
        control.setAttribute('aria-label',this.text(playing?'pause':'play')+': '+(item.caption||String(index+1)).slice(0,180));
      });
    }
    togglePreview(index) {
      const item=this.items[index]; if (!item) return;
      const current=this.track.querySelector('.is-active video');
      const play=index!==this.index || !current || current.paused;
      clearTimeout(this.scrollTimer); this.spotlightUserScroll = false;
      const changed = index !== this.index;
      this.previewChoices.set(item.id,play); this.index=index; this.applyCenter(changed); this.updatePreview();
    }
    updateSpotlightPreview() {
      if (!this.track || !this.items?.length) return;
      this.updateSoundButtons();
      const video=this.track.querySelector('.is-active video'),item=this.items[this.index];
      const permitted=()=>this.isConnected && video?.isConnected && video===this.track.querySelector('.is-active video') && this.items[this.index]?.id===item.id && (this.previewChoices.get(item.id)??this.motion) && this.inViewport && !document.hidden && !this.dialog?.open && !this.commerce?.pending && !this.commerce?.modal?.hasAttribute('open');
      this.track.querySelectorAll('video').forEach(other=>{
        if(other!==video){this.rememberPreviewTime(other);other.pause();other.muted=true;if(other.hasAttribute('src')){other.removeAttribute('src');other.load()}}
      });
      if (!video) return;
      video.muted=this.soundItem!==item.id; video.loop=true;
      if (!permitted()) {this.rememberPreviewTime(video);video.pause();this.updatePlaybackButtons();return;}
      // The large active player uses the complete video; short previews belong to thumbnails.
      const src=safeMedia(item.url); if (!src) return;
      if (video.getAttribute('src')!==src) {
        video.src=src;
        const restore=()=>{const time=this.previewTimes.get(item.id);if(Number.isFinite(time)&&time>0){try{video.currentTime=Number.isFinite(video.duration)?Math.min(time,Math.max(0,video.duration-.01)):time}catch{}}};
        if(video.readyState>=1)restore();else video.addEventListener('loadedmetadata',restore,{once:true,signal:this.renderAbort.signal});
      }
      if (video.paused) video.play().then(()=>{if(!permitted())video.pause();this.updatePlaybackButtons()}).catch(()=>this.updatePlaybackButtons());
      this.updatePlaybackButtons();
    }
    updatePreview() {
      if (this.mode === 'spotlight') { this.updateSpotlightPreview(); return; }
      if(this.mode==='stories') {
        if(!this.track)return;
        const box=this.track.getBoundingClientRect();
        for(const tile of this.track.children) {
          const video=tile.querySelector('video'),item=this.items[Number(tile.dataset.index)];if(!video||!item)continue;
          const permitted=()=>{const rect=tile.getBoundingClientRect();return this.isConnected&&video.isConnected&&this.motion&&!this.reduced.matches&&this.inViewport&&!document.hidden&&!this.dialog?.open&&!this.commerce?.pending&&!this.commerce?.modal?.hasAttribute('open')&&rect.right>box.left+12&&rect.left<box.right-12;};
          video.muted=true;video.loop=true;video.onended=null;
          if(!permitted()){video.pause();if(video.hasAttribute('src')){video.removeAttribute('src');video.load();}continue;}
          const src=safeMedia(item.preview||item.url);if(!src)continue;
          if(video.getAttribute('src')!==src)video.src=src;
          if(video.paused)video.play().then(()=>{if(!permitted())video.pause();}).catch(()=>{});
        }
        return;
      }
    }
    date(value) { if (!value) return ''; const date=new Date(value); return Number.isNaN(date.valueOf())?'':new Intl.DateTimeFormat(document.documentElement.lang||'en',{dateStyle:'medium'}).format(date); }
    click(event) {
      const target=event.target.closest('[data-action]'); if (!target || !this.contains(target)) return;
      const action=target.dataset.action;
      if (action==='open') this.open(Number(target.dataset.index),target);
      if (action==='preview-toggle' && this.mode==='spotlight') {event.preventDefault();event.stopPropagation();this.togglePreview(Number(target.dataset.index));}
      if (action==='previous'||action==='next') this.navigate(action==='next'?1:-1);
      if (action==='retry') this.load();
      if (action==='more') this.more();
      if (action==='story-more') this.scrollStories();
      if (action==='motion') { this.motion=!this.motion;this.updateMotionButton();this.updatePreview(); }
      if (action==='preview-sound' && this.mode==='spotlight') {
        event.preventDefault(); event.stopPropagation();
        const index=Number(target.dataset.index),item=this.items[index]; if (!item) return;
        this.soundItem=this.soundItem===item.id?null:item.id; this.updatePreview();
      }
      if (action==='close') this.dialog.close();
      if (action==='post-previous'||action==='post-next') this.movePost(action==='post-next'?1:-1,'[data-action="'+action+'"]');
      if (action==='album-previous'||action==='album-next') { const album=this.items[this.dialogIndex].album;this.albumIndex=wrap(this.albumIndex+(action==='album-next'?1:-1),album.length);this.renderDialog();this.dialog.querySelector('[data-action="'+action+'"]')?.focus({preventScroll:true}); }
      if (action==='configure' || action==='add') this.purchase(target);
    }
    open(index, opener) {
      if (this.socialBusy || !this.items[index]) return;
      this.opener=opener;this.dialogIndex=index;this.albumIndex=0;this.pauseAll();
      if (!this.dialog) {
        this.dialog=node('dialog','ssv2__dialog');this.dialog.setAttribute('aria-label',this.text('open'));
        this.append(this.dialog);
        this.listen(this.dialog,'close',()=>{this.dialogAbort?.abort();this.dialog.querySelectorAll('video').forEach(v=>{v.pause();v.removeAttribute('src');v.load();});this.dialog.replaceChildren();this.opener?.focus({preventScroll:true});this.updatePreview();});
        this.listen(this.dialog,'keydown',event=>{
          if (!['ArrowLeft','ArrowRight'].includes(event.key) || event.target.closest('select,input,textarea,video')) return;
          event.preventDefault(); const delta=event.key==='ArrowRight'?1:-1; const item=this.items[this.dialogIndex];
          if (item.album?.length) this.albumIndex=wrap(this.albumIndex+delta,item.album.length);
          else {this.movePost(delta);return;}
          this.renderDialog();this.dialog.querySelector('[data-action="close"]').focus();
        });
      }
      this.renderDialog();
      if (typeof this.dialog.showModal==='function') this.dialog.showModal();
      else { this.dialog.setAttribute('open','');this.dialog.setAttribute('role','region'); }
      this.dialog.querySelector('[data-action="close"]').focus();
      this.dialog.querySelector('video')?.play().catch(()=>{});
    }
    renderDialog() {
      this.dialogAbort?.abort();this.dialogAbort=new AbortController();
      const generation=(this.dialogGeneration||0)+1;this.dialogGeneration=generation;
      this.dialog.querySelectorAll('video').forEach(v=>{v.pause();v.removeAttribute('src');v.load();});
      this.dialog.replaceChildren();
      const item=this.items[this.dialogIndex],media=item.album?.length?item.album[this.albumIndex]:item;
      const toolbar=node('div','ssv2__dialogbar');toolbar.append(button(this.text('previous'),'post-previous'),button(this.text('next'),'post-next'),button(this.text('close'),'close'));this.dialog.append(toolbar);
      const body=node('div','ssv2__dialogbody');const visual=node('div','ssv2__visual');
      if (media.type==='video') {
        const video=node('video');video.controls=true;video.playsInline=true;video.preload='none';video.src=safeMedia(media.url);video.poster=safeMedia(media.poster);video.setAttribute('aria-label',(item.caption||this.text('watch')).slice(0,180));
        const caption=safeMedia(media.captions);if(caption){video.crossOrigin='anonymous';const track=node('track');track.kind='captions';track.srclang=media.captionLanguage||'en';track.label=media.captionLabel||this.text('captions');track.src=caption;video.append(track);}
        visual.append(video);
        if(this.mode!=='social')video.addEventListener('loadedmetadata',()=>{if(video.videoWidth>0&&video.videoHeight>0){video.style.maxWidth=video.videoWidth+'px';visual.style.setProperty('--ssv2-dialog-ratio',video.videoWidth/video.videoHeight);}},{signal:this.dialogAbort.signal});
        if(this.mode!=='social')this.bindStoryPlayer(video,visual);
        video.addEventListener('error',()=>{if(!visual.querySelector('[data-media-error]')){const error=node('p','',this.text('unavailable'));error.dataset.mediaError='';error.setAttribute('role','status');visual.append(error);}},{signal:this.dialogAbort.signal});
        if(this.mode!=='social')video.addEventListener('ended',()=>{if(this.dialog.open){this.dialogIndex=wrap(this.dialogIndex+1,this.items.length);this.renderDialog();}},{signal:this.dialogAbort.signal});
      } else {const img=this.image(media,item.caption?.slice(0,180)||'');img.loading='eager';img.style.objectFit='contain';visual.append(img);}
      if(item.album?.length){const nav=node('div','ssv2__albumnav');nav.append(button(this.text('previous'),'album-previous'),node('span','',`${this.albumIndex+1} / ${item.album.length}`),button(this.text('next'),'album-next'));visual.append(nav);}
      const details=node('div','ssv2__details');
      if(this.mode==='social'){const profile=node('div','ssv2__profile-row'),name=node('a','ssv2__profile-name',this.data.profile.name),follow=node('a','ssv2__follow',this.text('follow'));name.href=follow.href=safeSocial(this.data.profile.url);profile.append(name,follow);details.append(profile);}
      if(this.mode==='social'||!item.products?.length)details.append(node('p','ssv2__caption',item.caption));
      const original=this.mode==='social'?safeSocial(item.original):'';if(original){const a=node('a','ssv2__original',this.text('original'));a.href=original;details.append(a);}
      if(this.mode!=='social')for(const association of item.products||[]){const product=this.data.products[association.id];if(product){const card=this.productCard(product,association,generation);details.append(card);}}
      if(this.mode==='social')details.append(node('time','ssv2__post-date',this.date(item.date)));
      body.append(visual,details);this.dialog.append(body);
      if (this.dialog.open) this.dialog.querySelector('video')?.play().catch(()=>{});
      let touch;
      visual.addEventListener('pointerdown',event=>{if(event.target.closest('video,button'))return;touch={x:event.clientX,y:event.clientY};},{signal:this.dialogAbort.signal});
      visual.addEventListener('pointerup',event=>{if(!touch)return;const dx=event.clientX-touch.x,dy=event.clientY-touch.y;touch=null;if(Math.abs(dx)<60||Math.abs(dx)<Math.abs(dy)*1.5)return;const delta=dx<0?1:-1;if(item.album?.length)this.albumIndex=wrap(this.albumIndex+delta,item.album.length);else{this.movePost(delta);return;}this.renderDialog();},{signal:this.dialogAbort.signal});
    }
    bindStoryPlayer(video,visual) {
      const signal=this.dialogAbort.signal, sound=this.soundButton(this.dialogIndex,false);
      sound.dataset.action='story-sound';sound.classList.add('ssv2__storysound');video.muted=true;visual.append(sound);
      const updateSound=()=>{const audible=!video.muted&&video.volume>0;sound.setAttribute('aria-pressed',String(audible));sound.setAttribute('aria-label',this.text(audible?'mute':'unmute'));sound.querySelector('[data-sound-icon="muted"]').style.display=audible?'none':'';sound.querySelector('[data-sound-icon="audible"]').style.display=audible?'':'none';};
      sound.addEventListener('click',event=>{event.preventDefault();event.stopPropagation();video.muted=!video.muted;if(!video.muted&&video.volume===0)video.volume=1;updateSound();},{signal});
      video.addEventListener('volumechange',updateSound,{signal});updateSound();
      const onControls=event=>{const rect=video.getBoundingClientRect();return rect.height>0&&event.clientY>=rect.bottom-56;};
      let touch=null,swiped=false;
      video.addEventListener('pointerdown',event=>{swiped=false;if(!['touch','pen'].includes(event.pointerType)||onControls(event))return;touch={x:event.clientX,y:event.clientY,id:event.pointerId};},{signal});
      video.addEventListener('pointercancel',()=>{touch=null;},{signal});
      video.addEventListener('pointerup',event=>{
        if(!touch||touch.id!==event.pointerId)return;
        const dx=event.clientX-touch.x,dy=event.clientY-touch.y;touch=null;
        if(Math.abs(dx)<60||Math.abs(dx)<Math.abs(dy)*1.5)return;
        swiped=true;event.preventDefault();event.stopPropagation();this.movePost(dx<0?1:-1);
      },{signal});
      video.addEventListener('click',event=>{
        if(onControls(event))return;
        event.preventDefault();if(swiped){swiped=false;return;}
        if(video.paused)video.play().catch(()=>{});else video.pause();
      },{signal});
    }
    renderProductReviews(card, product, generation) {
      cart.reviews(this.root, product).then(data => {
        if (!data || this.dialogGeneration !== generation || !card.isConnected) return;
        const href = productPath(this.root, product.handle) + '#judgeme_product_reviews';
        if (data.rating) {
          const rating = node('a','ssv2__rating');rating.href=href;rating.setAttribute('aria-label',data.rating.value+' / 5 · '+data.rating.text);
          const stars = node('span','ssv2__rating-stars','★★★★★');stars.setAttribute('aria-hidden','true');
          const fill=node('span','ssv2__rating-fill','★★★★★');fill.style.width=(data.rating.value/5*100)+'%';stars.append(fill);
          rating.append(stars,node('span','ssv2__rating-count',data.rating.text));card.querySelector('.ssv2__productheading').append(rating);
        }
        if (data.highlight) {
          const h=data.highlight, figure=node('figure','stellar-customer-highlight ssv2__customer-highlight');figure.dataset.reviewProduct=product.id;
          if(h.label)figure.append(node('p','stellar-customer-highlight__label',h.label));
          figure.append(node('blockquote','',h.quote));const caption=node('figcaption','',h.attribution);
          if(h.readLabel){caption.append(document.createTextNode(' · '));const link=node('a','',h.readLabel);link.href=href;caption.append(link);}
          figure.append(caption);card.querySelector('.ssv2__productlinks').after(figure);
        }
      });
    }
    productCard(product, association, generation) {
      const card=node('div','ssv2__product');card.dataset.product=product.id;card._product=product;card._association=association;
      const heading=node('div','ssv2__productheading'),title=node('a','ssv2__producttitle',product.title);heading.append(title);card.append(heading);
      const price=node('p','ssv2__price');card.append(price);
      const label=node('label','ssv2__variant',this.text('option'));const select=node('select');select.dataset.variant='';select.disabled=true;label.append(select);card.append(label);
      const direct = this.mode === 'spotlight' || this.mode === 'stories';
      const add=button(this.text(direct?'add':'choose_options'),direct?'add':'configure');add.disabled=true;
      if (direct) {
        add.className='stellar-button stellar-button--brand stellar-button--active-hover stellar-button--layout stellar-button--large stellar-button--full ssv2__add product-form__submit button button--primary'; add.name='add';
        const label=node('span','',this.text('add')), icon=this.querySelector('template[data-add-icon]')?.content.cloneNode(true);
        if(icon)label.append(icon);add.replaceChildren(label);
      }
      card.append(add);
      const links=node('div','ssv2__productlinks');card.append(links);
      const learn=node('a','ssv2__learn',this.text('learn'));links.append(learn);
      const status=node('p','ssv2__productstatus',this.text('checking'));status.setAttribute('role','status');status.setAttribute('aria-live','polite');card.append(status);
      this.renderProductReviews(card,product,generation);
      cart.product(this.root,product,this.dialogAbort.signal).then(data=>{
        if(this.dialogGeneration!==generation||!this.isConnected)return;
        title.href=learn.href=productPath(this.root,product.handle);
        if(this.dataset.customerB2b==='true'){status.textContent=this.text('limit');return;}
        this.populateProduct(card,data);
      }).catch(error=>{
        if(error.name==='AbortError'||this.dialogGeneration!==generation||!this.isConnected)return;
        if(error.message==='product_unavailable'){card.remove();return;}
        status.textContent=this.text('product_error');
      });
      return card;
    }
    populateProduct(card,data) {
      card._current=data;const select=card.querySelector('select'),previous=select.value;select.replaceChildren();
      const allowed=card._association.variantIds||[];
      for(const variant of data.variants){if(allowed.length&&!allowed.map(String).includes(String(variant.id)))continue;const option=node('option','',variant.title+(variant.available?'':' - '+this.text('sold_out')));option.value=variant.id;option.disabled=!variant.available;select.append(option);}
      const first=[...select.options].find(o=>!o.disabled&&o.value===previous)||[...select.options].find(o=>!o.disabled);if(first)select.value=first.value;
      const onlyDefault=data.variants.length===1 && data.variants[0].title==='Default Title' && (!data.options?.length || data.options.every(option=>(typeof option==='string'?option:option.name)==='Title'));
      select.closest('.ssv2__variant').hidden=this.mode!=='social' && onlyDefault;
      select.disabled=!first||data.requires_selling_plan;this.variantChanged(select);
    }
    variantChanged(select) {
      const card=select.closest('.ssv2__product');const variant=card._current?.variants.find(v=>String(v.id)===select.value);
      const valid=variant?.available&&!card._current?.requires_selling_plan&&!cart.uncertain&&!cart.pending;
      card.querySelectorAll('[data-action="configure"],[data-action="add"]').forEach(button=>{
        button.disabled=!valid||!!this.commerce?.pending;
        if(button.dataset.action==='add')button.querySelector('span').firstChild.textContent=this.text(variant?.available?'add':'sold_out');
      });
      const href=productPath(this.root,card._product.handle)+(variant?'?variant='+encodeURIComponent(String(variant.id)):'');
      card.querySelectorAll('.ssv2__producttitle,.ssv2__learn').forEach(link=>link.href=href);
      card.querySelector('.ssv2__price').textContent=variant?this.money(variant.price):'';
      card.querySelector('.ssv2__productstatus').textContent=valid?(this.mode!=='social'?'':this.text('review_checkout')):this.text('limit');
    }
    money(value) {
      const amount=value/100, options={style:'currency',currency:this.dataset.currency||'AUD'};
      if(Number.isInteger(amount))options.minimumFractionDigits=0;
      return new Intl.NumberFormat(document.documentElement.lang||'en',options).format(amount);
    }
    async purchase(target) {
      if (target.disabled || this.commerce.pending || this.dataset.customerB2b === 'true') return;
      const card=target.closest('.ssv2__product'), select=card.querySelector('[data-variant]');
      const variant=card._current?.variants.find(v=>String(v.id)===select.value);
      if (!variant?.available || card._current?.requires_selling_plan) return;
      target.disabled=true;target.setAttribute('aria-busy','true');
      const status=card.querySelector('.ssv2__productstatus');status.textContent=this.text('loading');
      try { await this.commerce.open(card._product,variant.id,this.opener,target.dataset.action==='add'); }
      catch (error) { if (this.isConnected && card.isConnected) { if(error.message==='selection_changed'){variant.available=false;if(select.selectedOptions[0])select.selectedOptions[0].disabled=true;this.variantChanged(select);} status.textContent=this.text(error.message==='selection_changed'?'selection_changed':'options_error'); } }
      finally { target.removeAttribute('aria-busy');if(card.isConnected)target.disabled=!variant.available; }
    }

    disconnectedCallback() {
      this.storyResize?.disconnect();
      this.commerce?.destroy();
      this.generation++;this.pauseAll();this.lifecycle?.abort();this.renderAbort?.abort();this.dialogAbort?.abort();this.loadObserver?.disconnect();this.viewObserver?.disconnect();clearTimeout(this.scrollTimer);
      this.querySelectorAll('video').forEach(video=>{video.removeAttribute('src');video.load();});
      if(this.dialog?.open)this.dialog.close();
      this.data=null;this.loading=false;
    }
  }
  customElements.define('stellar-snapshot-v2', Snapshot);
})();
